// ============================================================================
// Canonical /dailys hub route contract
// ============================================================================
//
// `{view: 'webformhub', paths: ['/dailys/*']}` is a SPLAT row, and it is the
// ONLY row in the seven route tables with another view's EXACT row nested
// inside its own space: `{view: 'tasksWebform', paths: ['/dailys/tasks']}`.
// That nesting is what makes this fence necessary rather than prudent.
//
// `view.tasksWebform` is RELEASED now, which SHARPENS the hazard rather than
// retiring it. An unfenced hub would displace the Tasks mount instead of leaving
// the raw route for its own released claim or recovery disposition. Nothing
// about the fence changes — the routing fact it rests on is a property of the
// route table, not of either surface's former boot mode.
//
// WHAT GOES WRONG WITHOUT IT, MEASURED. With `view.webformhub` in the ledger
// and no fence, the real resolver answers RELEASED for
// `{view: 'webformhub', location: '/dailys/tasks'}` with
// `routedPathname: '/dailys'` — `routedLocationFor` refuses the raw path
// because `claimedExactlyByAnotherRow` sees the Tasks row, and canonicalizes
// onto the hub base. An unfenced RELEASED verdict would suppress recovery, so
// the released renderer would mount WebformHub at a URL that says
// `/dailys/tasks` and TasksWebform would never render. The same shape covers
// every view-led transition frame from a foreign pathname.
//
// This is not hypothetical: until view.webformhub's residual list emptied, the
// resolver's residual fence refused that exact input, and
// `authenticatedSurfaceResolver.test.ts` asserted it by name. The residual list
// was acting as the fence. Emptying it correctly — the surface really does own
// all of its reads now — removes that accident, so the fence has to be stated.
//
// AN ALLOWED SET, NOT A RESERVED-SEGMENT BLACKLIST. `WebformHub` is URL-driven
// over a closed list: `SUB_FORMS = ['broiler','layer','pig','cattle','egg',
// 'sheep']`, and `setActiveForm` navigates to `/dailys` or `/dailys/<sub>` and
// nothing else. So the canonical space is exactly those seven pathnames, and
// listing what IS owned refuses `tasks` without this module
// having to know the Tasks row exists — and refuses any FUTURE sibling row
// nested under `/dailys/` automatically. A blacklist would have to be kept in
// step with the live route tables, which is the failure mode a hardcoded
// one-segment matcher already caused once in this programme.
//
// The list is cross-checked against the presenter's own `SUB_FORMS` by
// tests/static/webform_hub_dailys_fence_static.test.js, so the two cannot
// drift.
//
// WHY IT IS SAFE TO REFUSE MORE THAN A RELEASE AUDIT WOULD NEED. Refusing a
// shape keeps it in the explicit recovery path. A later release lane may WIDEN
// this with browser evidence; it must never narrow it. In particular, an
// unknown descendant such as `/dailys/x/y` is not an audited released route, so
// refusing it cannot silently widen the hub capability.
//
// SEARCH AND HASH ARE NOT PART OF THE CONTRACT, deliberately, and that is the
// one place this differs from the weigh-in session record fence. That page
// READS `location.hash` to scroll a deep-linked comment into view, so a
// hash-carrying load is a materially different render. WebformHub reads
// `location.pathname` and nothing else — `useLocation()` is consulted once, for
// `activeForm` — so a query or fragment changes nothing about what renders, and
// excluding them would refuse shapes the surface genuinely serves identically.
// ============================================================================

export const WEBFORM_HUB_DAILYS_PATHNAME = '/dailys';
/**
 * The presenter's own sub-form list, which is the whole of the space
 * `setActiveForm` can navigate to. Frozen here rather than imported: this
 * module is boot-path code and must not pull a presenter (and everything it
 * imports) into the resolver's module graph. The static guard proves the two
 * lists are equal instead.
 */
export const WEBFORM_HUB_DAILYS_SUB_FORMS: readonly string[] = Object.freeze([
  'broiler',
  'layer',
  'pig',
  'cattle',
  'egg',
  'sheep',
]);

const WEBFORM_HUB_DAILYS_PATHNAMES: ReadonlySet<string> = new Set([
  WEBFORM_HUB_DAILYS_PATHNAME,
  ...WEBFORM_HUB_DAILYS_SUB_FORMS.map((subForm) => `${WEBFORM_HUB_DAILYS_PATHNAME}/${subForm}`),
]);

/**
 * `/dailys`, or `/dailys/<sub>` for exactly one of the six sub-forms — seven
 * literal pathnames, and that is the whole predicate.
 *
 * IT TOOK TWO ROUNDS OF NEGATIVE CONTROLS TO GET HERE. The first draft copied
 * the weigh-in session record fence, which accepts any single opaque id and
 * therefore needs a raw-segment safety check (no separator, NUL, line break or
 * query marker) and a one-shot `decodeURIComponent` to keep encoded separators
 * and dot segments out. Both clauses are gone, because a control proved each
 * one unfalsifiable against a fixed allowed set:
 *
 *   * no segment carrying a separator or a control character equals one of the
 *     six names, so the membership test already refused every input the raw
 *     check refused; and
 *   * dropping the decode only made the predicate STRICTER — it stopped
 *     admitting `/dailys/%65gg` as `egg` — the stricter and safer answer for
 *     an unreviewed raw route.
 *
 * An unfalsifiable clause in a fence is worse than no clause: it reads as
 * protection. What is left is falsifiable in every direction, and
 * tests/static/webform_hub_dailys_fence_static.test.js exercises both.
 */
export function isCanonicalWebformHubDailysPathname(pathname: unknown): pathname is string {
  return typeof pathname === 'string' && WEBFORM_HUB_DAILYS_PATHNAMES.has(pathname);
}

/**
 * The resolver hands this the raw location, which may be a bare pathname string
 * or a location-like object. Only the pathname is consulted; see the header for
 * why search and hash are deliberately outside this contract.
 */
export function isCanonicalWebformHubDailysLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalWebformHubDailysPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;
  return isCanonicalWebformHubDailysPathname((location as Readonly<{pathname?: unknown}>).pathname);
}
